import Lozenge from '@atlaskit/lozenge/lozenge';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { useMessageContext } from '@atlassian/post-office-context/message-context';

import { useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import { AdControls } from '@post-office/components--ad-controls-sequoia';

import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';

import DashboardIcon from '@atlaskit/icon/core/dashboard';

import { type JSX } from 'react/jsx-runtime';

interface PlacementRenderProps {
	dismiss: (messageInstanceId: string) => void;
	onItemClick?: () => void;
	isSelected?: boolean;
}

export type AssetsPremiumTrialDashboardItemProps = PlacementRenderProps;

const whyAmISeeingThisModalContent = {
	title: (
		<FormattedMessage
			id="post-office.jsm-assets-premium-trial-dashboard-item.reasons-modal-title"
			defaultMessage="Why am I seeing this?"
			description="Title of the reasons modal explaining why the premium trial is being shown"
		/>
	),
	message: (
		<FormattedMessage
			id="post-office.jsm-assets-premium-trial-dashboard-item.reasons-modal-explanation"
			defaultMessage="We think this premium feature will help you get more out of Assets. The recommendation is based on:"
			description="Explanation as to why the premium trial is being shown"
		/>
	),
	reasons: [
		<FormattedMessage
			key="reason-1"
			id="post-office.jsm-assets-premium-trial-dashboard-item.reasons-modal-bullet-one"
			defaultMessage="your current product usage"
			description="First bullet point of the reasons modal"
		/>,
		<FormattedMessage
			key="reason-2"
			id="post-office.jsm-assets-premium-trial-dashboard-item.reasons-modal-bullet-two"
			defaultMessage="features available in premium"
			description="Second bullet point of the reasons modal"
		/>,
	],
};

export const AssetsPremiumTrialDashboardItem = ({
	dismiss,
	onItemClick,
	isSelected,
}: AssetsPremiumTrialDashboardItemProps): JSX.Element => {
	const { onCtaActioned, onClosed } = usePostOfficeUserInteractions();
	const { messageInstanceId } = useMessageContext();

	const handleClick = useCallback(() => {
		void onCtaActioned({
			interactionIdentifier: 'try-premium-trial',
			destinationUrl: '/jira/assets/dashboards',
		});

		// Call the parent's click handler instead of navigating
		onItemClick?.();
	}, [onCtaActioned, onItemClick]);

	const handleDismiss = useCallback(() => {
		void onClosed({ interactionIdentifier: 'dismiss' });

		if (messageInstanceId) {
			dismiss(messageInstanceId);
		}
	}, [dismiss, onClosed, messageInstanceId]);

	return (
		<LinkMenuItem
			elemBefore={<DashboardIcon label="" />}
			elemAfter={
				<Lozenge appearance="new">
					<span id="assets-premium-trial-dashboard-item-lozenge-try">
						<FormattedMessage
							id="post-office.jsm-assets-premium-trial-dashboard-item.lozenge-try"
							defaultMessage="Try"
							description="A lozenge to display the try button for premium trial"
						/>
					</span>
				</Lozenge>
			}
			href="/jira/assets/dashboards"
			onClick={handleClick}
			actionsOnHover={
				<AdControls
					iconSize="small"
					spacing="compact"
					onDismissed={handleDismiss}
					showWhyAmISeeingThis={true}
					whyAmISeeingThisModalContent={whyAmISeeingThisModalContent}
					ariaLabelledby="assets-premium-trial-dashboard-item-message-title"
				/>
			}
			isSelected={isSelected}
		>
			<span id="assets-premium-trial-dashboard-item-message-title">
				<FormattedMessage
					id="post-office.jsm-assets-premium-trial-dashboard-item.menu-item-label"
					defaultMessage="Dashboards"
					description="Menu item label for the Dashboard premium trial"
				/>
			</span>
		</LinkMenuItem>
	);
};
